import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';
import { theme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, SvgIcon, TableSortLabel, Tooltip } from '@mui/material';
import { Trash01, Copy06 } from '../../../assets';
import { StockCheckScheduleDetail } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';
import { useContext, useState } from 'react';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import SubmitModal from './ScheduleDeleteModal';
export default function DenseTable(props: {
  readonly list: StockCheckScheduleDetail[], readonly storeID: string, readonly loadData: () => void, readonly searchTerm: string
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { deleteSchedule, duplicateStockCheck } = useNswagClient();
  const [page, setPage] = React.useState(0);
  const [openModal, setOpenModal] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(-1);
  const [data, setData] = React.useState(props.list);
  const rowsPerPage = 10;
  const { hasPermissionTo } = useContext(UserContext);
  const [showResultMessage, setShowResultMessage] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  //Header sort
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('name'); // Set the default column to be sorted
  const { selectedStore } = useContext(UserContext);

  const hasRightToEdit: boolean = hasPermissionTo(['StockCountScheduleWrite']);

  const isAsc = order === 'asc';

  const handleSort = (property: string) => {
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    handleSearchChange();
  };

  const filteredData = props.list.filter(x => x.name?.toLowerCase().includes(props.searchTerm.toLowerCase()));

  const sortedData = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const comparator = (a: any, b: any) => {
      if (typeof a[orderBy] === 'string' || a[orderBy] instanceof Date) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (isAsc ? a[orderBy].localeCompare(b[orderBy]) : b[orderBy].localeCompare(a[orderBy]));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]) as any;
      }
    };
    return filteredData.sort(comparator);
  };

  const handleSearchChange = () => {
    setData(sortedData());
  };

  React.useEffect(() => handleSearchChange(), [props.searchTerm]);

  const handleCancelModal = () => {
    setOpenModal(false);
  };

  const submitButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    setDeleteId(id);
    setOpenModal(true);
  };

  const handleConfirmModal = () => {
    deleteSchedule(props.storeID, deleteId).then(props.loadData);
  };

  const handleDuplicate = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();

    try {
      await duplicateStockCheck(selectedStore?.storeNumber ?? '-1', id);
      
      setShowResultMessage({
        open: true,
        message: t('stockcounts.duplicateStockCreateSuccess'),
        severity: 'success',
      });
      setTimeout(() => {
        props.loadData();
      }, 4000);
    } catch (error) {
      setShowResultMessage({
        open: true,
        message: t('stockcounts.duplicateStockCreateError'),
        severity: 'error',
      });
    }
  };

  const maxPages = () => {
    return Math.ceil(data.length / rowsPerPage);
  };

  const visibleRows = () => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const closeProcessingAlert = () => {
    setShowResultMessage({ ...showResultMessage, open: false });
  };

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: theme.palette.custom.gray[200],
    }}>
     
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={order}
                  onClick={() => handleSort('name')}
                >
                  {t('name')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'isActive'}
                  direction={order}
                  onClick={() => handleSort('isActive')}
                >
                  {t('active')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                {t('remove')}
              </TableCell>
              <TableCell align="left">
                {t('duplicate')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows().map((row) => (
              <TableRow
                hover
                key={row.id}
                onClick={() => navigate(`/create-edit-schedule-stock-count/${row.id}`)}
              >
                <TableCell>
                  {row.name}
                </TableCell>
                <TableCell>
                  {String(row.isActive)}
                </TableCell>
                <TableCell>
                  <Tooltip title='Delete stock count schedule'
                    color='dark'
                    placement='top'>
                    <IconButton
                      disabled={!hasRightToEdit}
                      size='small'
                      onClick={(event) => submitButtonHandler(event, row.id ?? -1)}>
                      <SvgIcon color={!hasRightToEdit ? 'action' : 'error'}><Trash01></Trash01></SvgIcon>
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title='Duplicate schedule'
                    color='dark'
                    placement='top'>
                    <IconButton
                      disabled={!hasRightToEdit}
                      size='small'
                      onClick={(event) => handleDuplicate(event, row.id ?? -1)}>
                      <SvgIcon color={!hasRightToEdit ? 'action' : 'primary'}><Copy06 /></SvgIcon>
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={maxPages()}
      ></CustomPagination>

      <SubmitModal openModal={openModal}
        onConfirm={handleConfirmModal}
        onCancel={handleCancelModal}></SubmitModal>
      <MessagesSnackbar
        open={showResultMessage.open}
        onClose={closeProcessingAlert}
        message={showResultMessage.message}
        severity={showResultMessage.severity}
        duration={null}
      />
    </Box>
  );
}
